import React, { useState, useEffect } from 'react';
import './TemplateBuilder.css';

// Types for elements
const ELEMENT_TYPES = {
  MAIN_HEADER: '_main-header',
  HEADER_IMAGE: '_header-image',
  SECTION: '_section',
  MAIN_BODY_COPY: '_main-body-copy',
  BODY_IMAGE: '_body-image',
  ACCORDION: '_accordion',
  YOUTUBE_VIDEO: '_youtube-video',
  WIDGET_BLOCK: '_widget-block',
  RAW_HTML: '_raw-html',
  CTA_BUTTON: '_cta-button',
};

// Component type definitions for modal
const COMPONENT_TYPES = [
  { id: ELEMENT_TYPES.MAIN_HEADER, label: 'Main Header', icon: 'H' },
  { id: ELEMENT_TYPES.HEADER_IMAGE, label: 'Header Image', icon: '<i class="fas fa-image"></i>' },
  { id: ELEMENT_TYPES.SECTION, label: 'Section Title', icon: 'T' },
  { id: ELEMENT_TYPES.MAIN_BODY_COPY, label: 'Main Body Copy', icon: '¶' },
  { id: ELEMENT_TYPES.BODY_IMAGE, label: 'Body Section', icon: '▣' },
  { id: ELEMENT_TYPES.ACCORDION, label: 'Accordion', icon: '⌄' },
  { id: ELEMENT_TYPES.YOUTUBE_VIDEO, label: 'YouTube Video', icon: '▶' },
  { id: ELEMENT_TYPES.WIDGET_BLOCK, label: 'Widget Block', icon: '◯' },
  { id: ELEMENT_TYPES.RAW_HTML, label: 'Raw HTML', icon: '</>' },
  { id: ELEMENT_TYPES.CTA_BUTTON, label: 'CTA Button', icon: '🔗' },
];

function generateId() {
  return Math.random().toString(36).substr(2, 9);
}

const TemplateBuilder = () => {
  const [elements, setElements] = useState<any[]>([]);
  const [inputs, setInputs] = useState({
    mainHeader: '',
    headerImage: '',
    headerImageAlt: '',
    sectionTitle: '',
    mainBodyCopy: '',
    mainBodyCopyOutputType: 'paragraph', // 'paragraph' | 'list'
    bodyImage: '',
    bodyImageAlt: '',
    bodyCopyTitle: '',
    bodyCopy: '',
    bodyCopyOutputType: 'paragraph', // 'paragraph' | 'list'
    accordionTitle: '',
    accordionCopy: '',
    accordionCopyOutputType: 'paragraph', // 'paragraph' | 'list'
    youtubeUrl: '',
    widgetField: '',
    rawHtml: '',
    ctaButtonLabel: '',
    ctaButtonUrl: '',
  });
  const [copied, setCopied] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingOutputType, setEditingOutputType] = useState<'paragraph' | 'list' | 'center' | 'html'>('paragraph');
  const [openAccordions, setOpenAccordions] = useState<{ [id: string]: boolean }>({});
  const [hoveredElements, setHoveredElements] = useState<{ [id: string]: boolean }>({});
  const [isSelectingText, setIsSelectingText] = useState(false);
  
  // Global text selection detection
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      // Small delay to allow text selection to complete
      setTimeout(() => {
        setIsSelectingText(false);
      }, 100);
    };

    const handleGlobalSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        setIsSelectingText(true);
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('selectionchange', handleGlobalSelectionChange);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('selectionchange', handleGlobalSelectionChange);
    };
  }, []);
  
  // New state for modal functionality
  const [showComponentList, setShowComponentList] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [modalInputs, setModalInputs] = useState({
    mainHeader: '',
    headerImage: '',
    headerImageAlt: '',
    sectionTitle: '',
    mainBodyCopy: '',
    mainBodyCopyOutputType: 'paragraph',
    bodyImage: '',
    bodyImageAlt: '',
    bodyCopyTitle: '',
    bodyCopy: '',
    bodyCopyOutputType: 'paragraph',
    accordionTitle: '',
    accordionCopy: '',
    accordionCopyOutputType: 'paragraph',
    youtubeUrl: '',
    widgetField: '',
    rawHtml: '',
    ctaButtonLabel: '',
    ctaButtonUrl: '',
  });

  // Handle input changes for text fields and toggles
  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle modal input changes
  const handleModalInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setModalInputs(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Open component selection modal
  const openComponentList = () => {
    setShowComponentList(true);
  };

  // Close component selection modal
  const closeComponentList = () => {
    setShowComponentList(false);
  };

  // Select component and open input modal
  const selectComponent = (componentType: string) => {
    setSelectedComponent(componentType);
    setShowComponentList(false);
    setShowModal(true);
    // Reset modal inputs
    setModalInputs({
      mainHeader: '',
      headerImage: '',
      headerImageAlt: '',
      sectionTitle: '',
      mainBodyCopy: '',
      mainBodyCopyOutputType: 'paragraph',
      bodyImage: '',
      bodyImageAlt: '',
      bodyCopyTitle: '',
      bodyCopy: '',
      bodyCopyOutputType: 'paragraph',
      accordionTitle: '',
      accordionCopy: '',
      accordionCopyOutputType: 'paragraph',
      youtubeUrl: '',
      widgetField: '',
      rawHtml: '',
      ctaButtonLabel: '',
      ctaButtonUrl: '',
    });
  };

  // Close input modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedComponent(null);
  };

  // Add element with outputType data if applicable
  const addElement = (type: string) => {
    let data: any = {};
    switch (type) {
      case ELEMENT_TYPES.MAIN_HEADER:
        data = { title: modalInputs.mainHeader };
        break;
      case ELEMENT_TYPES.HEADER_IMAGE:
        data = { url: modalInputs.headerImage, alt: modalInputs.headerImageAlt };
        break;
      case ELEMENT_TYPES.SECTION:
        data = { title: modalInputs.sectionTitle };
        break;
      case ELEMENT_TYPES.MAIN_BODY_COPY:
        data = { copy: modalInputs.mainBodyCopy, outputType: modalInputs.mainBodyCopyOutputType };
        break;
      case ELEMENT_TYPES.BODY_IMAGE:
        data = {
          url: modalInputs.bodyImage,
          alt: modalInputs.bodyImageAlt,
          title: modalInputs.bodyCopyTitle,
          copy: modalInputs.bodyCopy,
          outputType: modalInputs.bodyCopyOutputType,
        };
        break;
        case ELEMENT_TYPES.ACCORDION:
          data = {
            title: modalInputs.accordionTitle,
            copy: modalInputs.accordionCopy,
            outputType: modalInputs.accordionCopyOutputType,
          };
          break;
      case ELEMENT_TYPES.YOUTUBE_VIDEO:
        data = { url: modalInputs.youtubeUrl };
        break;

      case ELEMENT_TYPES.WIDGET_BLOCK:
        data = { field: modalInputs.widgetField };
        break;
      case ELEMENT_TYPES.RAW_HTML:
        data = { html: modalInputs.rawHtml };
        break;
      case ELEMENT_TYPES.CTA_BUTTON:
        data = { label: modalInputs.ctaButtonLabel, url: modalInputs.ctaButtonUrl };
        break;
    }
    setElements(prev => [...prev, { id: generateId(), type, data }]);
    closeModal();
  };

  const removeElement = (id: string) => {
    setElements(prev => prev.filter(el => el.id !== id));
  };

  const moveElementUp = (id: string) => {
    setElements(prev => {
      const index = prev.findIndex(el => el.id === id);
      if (index > 0) {
        const newElements = [...prev];
        [newElements[index - 1], newElements[index]] = [newElements[index], newElements[index - 1]];
        return newElements;
      }
      return prev;
    });
  };

  const moveElementDown = (id: string) => {
    setElements(prev => {
      const index = prev.findIndex(el => el.id === id);
      if (index < prev.length - 1) {
        const newElements = [...prev];
        [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
        return newElements;
      }
      return prev;
    });
  };

  const handleEditStart = (id: string, value: string, outputType?: 'paragraph' | 'list' | 'center' | 'html') => {
    setEditingId(id);
    setEditingValue(value);
    if (outputType) {
      setEditingOutputType(outputType);
    } else {
      setEditingOutputType('paragraph');
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditingValue(e.target.value);
  };

  const handleEditOutputTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value as 'paragraph' | 'list' | 'center' | 'html';
    setEditingOutputType(val);
  };

  const handleEditSave = (id: string, field: string) => {
    setElements(prev =>
      prev.map(el => {
        if (el.id === id) {
          if (
            field === 'copy' &&
            (el.type === ELEMENT_TYPES.MAIN_BODY_COPY ||
              el.type === ELEMENT_TYPES.BODY_IMAGE ||
              el.type === ELEMENT_TYPES.ACCORDION)
          ) {
            return { ...el, data: { ...el.data, copy: editingValue, outputType: editingOutputType } };
          }
          return { ...el, data: { ...el.data, [field]: editingValue } };
        }
        return el;
      })
    );
    setEditingId(null);
    setEditingValue('');
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, id: string, field: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave(id, field);
    }
  };

  const toggleAccordion = (id: string) => {
    setOpenAccordions(prev => {
      const isOpen = !!prev[id];
      // Close all, then open the clicked one if it was closed
      return isOpen ? {} : { [id]: true };
    });
  };

  // Handle element hover for remove button visibility
  const handleElementMouseEnter = (id: string) => {
    setHoveredElements(prev => ({ ...prev, [id]: true }));
  };

  const handleElementMouseLeave = (id: string) => {
    setHoveredElements(prev => ({ ...prev, [id]: false }));
  };

  const handleElementTouchStart = (id: string) => {
    setHoveredElements(prev => ({ ...prev, [id]: true }));
  };

  // Handle text selection detection
  const handleMouseDown = (e: React.MouseEvent) => {
    // Check if clicking on text content
    const target = e.target as HTMLElement;
    if (target.tagName === 'P' || target.tagName === 'H1' || target.tagName === 'H2' || target.tagName === 'H3' || 
        target.classList.contains('_main-body-copy') || target.classList.contains('_body-copy') || 
        target.classList.contains('_accordion-copy')) {
      setIsSelectingText(true);
    }
  };

  const handleMouseUp = () => {
    setIsSelectingText(false);
  };


  // Helper to render text or list based on outputType
  const renderTextOrList = (text: string, outputType: 'paragraph' | 'list' | 'center' | 'html') => {
    if (!text) return null;
    if (outputType === 'html') {
      return (
        <span className="_html-output" style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: text }} />
      );
    }
    if (outputType === 'list') {
      // Split text by lines, trim bullet characters if present
      const lines = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      return (
        <ul className="_list-output">
          {lines.map((line, i) => {
            // Remove common bullet characters if present
            const cleaned = line.replace(/^[-•*]\s*/, '');
            return <li key={i}>{cleaned}</li>;
          })}
        </ul>
      );
    }
    // paragraph/center mode: preserve line breaks as <br />
    const content = text.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i !== text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
    return (
      <span style={{ display: 'block', textAlign: outputType === 'center' ? 'center' : 'left' }}>
        {content}
      </span>
    );
  };

  const renderElement = (el: any, index?: number, totalElements?: number) => {
    const elementContent = (() => {
      switch (el.type) {
        case ELEMENT_TYPES.MAIN_HEADER:
          return editingId === el.id ? (
            <input
              className="_main-header editing"
              value={editingValue}
              autoFocus
              onChange={handleEditChange}
              onBlur={() => handleEditSave(el.id, 'title')}
              onKeyDown={e => handleEditKeyDown(e, el.id, 'title')}
            />
          ) : (
            <h1
              className="_main-header"
              key={el.id}
              onClick={() => handleEditStart(el.id, el.data.title)}
              style={{ cursor: 'pointer' }}
            >
              {el.data.title}
            </h1>
          );
        case ELEMENT_TYPES.HEADER_IMAGE:
          return <img className="_header-image" key={el.id} src={el.data.url} alt={el.data.alt || "Header"} />;
        case ELEMENT_TYPES.SECTION:
          return editingId === el.id ? (
            <input
              className="_section-title editing"
              value={editingValue}
              autoFocus
              onChange={handleEditChange}
              onBlur={() => handleEditSave(el.id, 'title')}
              onKeyDown={e => handleEditKeyDown(e, el.id, 'title')}
            />
          ) : (
            <h2
              className="_section-title"
              key={el.id}
              onClick={() => handleEditStart(el.id, el.data.title)}
              style={{ cursor: 'pointer' }}
            >
              {el.data.title}
            </h2>
          );
        case ELEMENT_TYPES.MAIN_BODY_COPY:
          return editingId === el.id ? (
            <div className="_main-body-copy editing">
              <textarea
                value={editingValue}
                autoFocus
                onChange={handleEditChange}
                onBlur={() => handleEditSave(el.id, 'copy')}
                onKeyDown={e => handleEditKeyDown(e, el.id, 'copy')}
                placeholder="Enter main body copy"
                rows={4}
              />
              <div className="_output-type-selector">
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="paragraph"
                    checked={editingOutputType === 'paragraph'}
                    onChange={handleEditOutputTypeChange}
                  />
                  Paragraph
                </label>
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="list"
                    checked={editingOutputType === 'list'}
                    onChange={handleEditOutputTypeChange}
                  />
                  List
                </label>
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="center"
                    checked={editingOutputType === 'center'}
                    onChange={handleEditOutputTypeChange}
                  />
                  Center
                </label>
              </div>
            </div>
          ) : (
            <p
              className="_main-body-copy"
              key={el.id}
              onClick={() => handleEditStart(el.id, el.data.copy, el.data.outputType || 'paragraph')}
              style={{ cursor: 'pointer', whiteSpace: 'pre-wrap' }}
            >
              {renderTextOrList(el.data.copy, el.data.outputType || 'paragraph')}
            </p>
          );
        case ELEMENT_TYPES.BODY_IMAGE:
          return (
            <div className="_body-image-block" key={el.id}>
              <img className="_body-image" src={el.data.url} alt={el.data.alt || "Body"} />
              <div className="_body-copy-container">
                {el.data.title && <h3 className="_body-copy-title">{el.data.title}</h3>}
                {editingId === el.id ? (
                  <div className="_body-copy editing">
                    <textarea
                      value={editingValue}
                      autoFocus
                      onChange={handleEditChange}
                      onBlur={() => handleEditSave(el.id, 'copy')}
                      onKeyDown={e => handleEditKeyDown(e, el.id, 'copy')}
                      placeholder="Enter body copy"
                      rows={4}
                    />
                    <div className="_output-type-selector">
                      <label>
                        <input
                          type="radio"
                          name="bodyCopyOutputType"
                          value="paragraph"
                          checked={editingOutputType === 'paragraph'}
                          onChange={handleEditOutputTypeChange}
                        />
                        Paragraph
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="bodyCopyOutputType"
                          value="list"
                          checked={editingOutputType === 'list'}
                          onChange={handleEditOutputTypeChange}
                        />
                        List
                      </label>
                    </div>
                  </div>
                ) : (
                  <div
                    className="_body-copy"
                    onClick={() => handleEditStart(el.id, el.data.copy, el.data.outputType || 'paragraph')}
                    style={{ cursor: 'pointer', whiteSpace: 'pre-wrap' }}
                  >
                    {renderTextOrList(el.data.copy, el.data.outputType || 'paragraph')}
                  </div>
                )}
              </div>
            </div>
          );

          case ELEMENT_TYPES.ACCORDION:
            return (
              <div className="_accordion" key={el.id}>
                <div className="_accordion-header" onClick={() => toggleAccordion(el.id)}>
                  <span className="_accordion-title">{el.data.title}</span>
                  <button
                    className={
                      '_accordion-toggle' + (openAccordions[el.id] ? ' _accordion-toggle--active' : '')
                    }
                    aria-label={openAccordions[el.id] ? 'Collapse' : 'Expand'}
                  >
                    {openAccordions[el.id] ? '-' : '+'}
                  </button>
                </div>
                {openAccordions[el.id] && (
                  <div className="_accordion-body">
                    {editingId === el.id ? (
                      <div className="_accordion-copy editing">
                        <textarea
                          value={editingValue}
                          autoFocus
                          onChange={handleEditChange}
                          onBlur={() => handleEditSave(el.id, 'copy')}
                          onKeyDown={e => handleEditKeyDown(e, el.id, 'copy')}
                          placeholder="Enter accordion copy"
                          rows={4}
                        />
                <div className="_output-type-selector">
                          <label>
                            <input
                              type="radio"
                              name="accordionCopyOutputType"
                              value="paragraph"
                              checked={editingOutputType === 'paragraph'}
                              onChange={handleEditOutputTypeChange}
                            />
                            Paragraph
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="accordionCopyOutputType"
                              value="list"
                              checked={editingOutputType === 'list'}
                              onChange={handleEditOutputTypeChange}
                            />
                            List
                          </label>
                  <label>
                    <input
                      type="radio"
                      name="accordionCopyOutputType"
                      value="html"
                      checked={editingOutputType === 'html'}
                      onChange={handleEditOutputTypeChange}
                    />
                    HTML
                  </label>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="_accordion-copy"
                        onClick={() => handleEditStart(el.id, el.data.copy, el.data.outputType || 'paragraph')}
                        style={{ cursor: 'pointer', whiteSpace: el.data.outputType === 'html' ? 'normal' : 'pre-wrap' }}
                      >
                        {renderTextOrList(el.data.copy, el.data.outputType || 'paragraph')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );

        case ELEMENT_TYPES.YOUTUBE_VIDEO: {
          // Extract video ID from URL
          const match = el.data.url.match(
            /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([\w-]{11})/
          );
          const videoId = match ? match[1] : null;
          return videoId ? (
            <div className="_youtube-video" key={el.id}>
              <iframe
                width="100%"
                height="382"
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ maxWidth: '680px', width: '100%' }}
              ></iframe>
            </div>
          ) : null;
        };
        
        case ELEMENT_TYPES.WIDGET_BLOCK:
          return (
            <div
              style={{ maxWidth: '980px', width: '100%', margin: '0 auto 64px', display: 'flex' }}
              key={el.id}
            >
              <div id="Trending_Products" className="_tabcontent _widget_item_section">
                {'{{WidgetTemplate=PB-Homepage-Widget,' + el.data.field + '}}'}
              </div>
            </div>
          );
        case ELEMENT_TYPES.RAW_HTML:
          return (
            <div className="_raw-html-block" key={el.id}>
              <div
                className="_raw-html-content"
                dangerouslySetInnerHTML={{ __html: el.data.html }}
              />
   
            </div>
          );
        case ELEMENT_TYPES.CTA_BUTTON:
          return (
            <div className="_cta-button-container" key={el.id}>
              <a href={el.data.url} className="_cta-button">
                {el.data.label}
              </a>
            </div>
          );
        default:
          return null;
      }
    })();

    const canMoveUp = index !== undefined && index > 0;
    const canMoveDown = index !== undefined && totalElements !== undefined && index < totalElements - 1;

    return (
      <div 
        className={`_element-container ${isSelectingText ? 'selecting-text' : ''}`}
        key={el.id}
        onMouseEnter={() => !isSelectingText && handleElementMouseEnter(el.id)}
        onMouseLeave={() => !isSelectingText && handleElementMouseLeave(el.id)}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={() => handleElementTouchStart(el.id)}
      >
        {elementContent}
        <div 
          className="_element-controls"
          onMouseEnter={(e) => e.stopPropagation()}
          onMouseLeave={(e) => e.stopPropagation()}
        >
          <button 
            className={`_move-element-btn _move-element-btn--up arrow-btn ${hoveredElements[el.id] ? '_move-element-btn--visible' : ''} ${!canMoveUp ? '_move-element-btn--disabled' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (canMoveUp) moveElementUp(el.id);
            }}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            aria-label="Move element up"
            disabled={!canMoveUp}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="18,15 12,9 6,15"/>
            </svg>
          </button>
          <button 
            className={`_move-element-btn _move-element-btn--down arrow-btn ${hoveredElements[el.id] ? '_move-element-btn--visible' : ''} ${!canMoveDown ? '_move-element-btn--disabled' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (canMoveDown) moveElementDown(el.id);
            }}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            aria-label="Move element down"
            disabled={!canMoveDown}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6,9 12,15 18,9"/>
            </svg>
          </button>
          <button 
            className={`_remove-element-btn ${hoveredElements[el.id] ? '_remove-element-btn--visible' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              removeElement(el.id);
            }}
            onMouseEnter={(e) => e.stopPropagation()}
            onMouseLeave={(e) => e.stopPropagation()}
            aria-label="Remove element"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    const groupedElements = [];
    let currentGroup = [];
    
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      
      if (element.type === ELEMENT_TYPES.BODY_IMAGE) {
        // Add to current group
        currentGroup.push(element);
      } else {
        // If we have a group, add it to groupedElements
        if (currentGroup.length > 0) {
          groupedElements.push({
            type: 'group',
            elements: currentGroup
          });
          currentGroup = [];
        }
        // Add the non-body-image element
        groupedElements.push(element);
      }
    }
    
    // Handle any remaining group at the end
    if (currentGroup.length > 0) {
      groupedElements.push({
        type: 'group',
        elements: currentGroup
      });
    }
    
    return (
      <div className="template-preview">
        {groupedElements.map((item, index) => {
          if (item.type === 'group') {
            return (
              <div key={`group-${index}`} className="_body-block-group">
                {item.elements.map((el: any, groupIndex: number) => {
                  // Find the actual index in the flat elements array
                  const flatIndex = elements.findIndex(e => e.id === el.id);
                  return renderElement(el, flatIndex, elements.length);
                })}
              </div>
            );
          } else {
            // Find the actual index in the flat elements array
            const flatIndex = elements.findIndex(e => e.id === item.id);
            return renderElement(item, flatIndex, elements.length);
          }
        })}
      </div>
    );
  };

  // Render component selection modal
  const renderComponentListModal = () => {
    if (!showComponentList) return null;
    
    return (
      <div className="_modal-overlay" onClick={closeComponentList}>
        <div className="_modal-content" onClick={e => e.stopPropagation()}>
          <div className="_modal-header">
            <h3>Select Component Type</h3>
            <button className="_modal-close" onClick={closeComponentList}>×</button>
          </div>
          <div className="_component-grid">
            {COMPONENT_TYPES.map(component => (
              <button
                key={component.id}
                className="_component-option"
                onClick={() => selectComponent(component.id)}
              >
                <span className="_component-icon" dangerouslySetInnerHTML={
                  component.id === ELEMENT_TYPES.HEADER_IMAGE 
                    ? { __html: '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>' }
                    : { __html: component.icon }
                }></span>
                <span className="_component-label">{component.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Render input modal
  const renderInputModal = () => {
    if (!showModal || !selectedComponent) return null;

    const renderInputFields = () => {
      switch (selectedComponent) {
        case ELEMENT_TYPES.MAIN_HEADER:
          return (
            <div className="_form-group">
              <label>Main Header Title</label>
              <input
                name="mainHeader"
                value={modalInputs.mainHeader}
                onChange={handleModalInput}
                placeholder="Enter main header title"
              />
            </div>
          );

        case ELEMENT_TYPES.HEADER_IMAGE:
          return (
            <>
              <div className="_form-group">
                <label>Header Image URL</label>
                <input
                  name="headerImage"
                  value={modalInputs.headerImage}
                  onChange={handleModalInput}
                  placeholder="Enter image URL"
                />
              </div>
              <div className="_form-group">
                <label>Alt Text</label>
                <input
                  name="headerImageAlt"
                  value={modalInputs.headerImageAlt}
                  onChange={handleModalInput}
                  placeholder="Enter alt text for accessibility"
                />
              </div>
            </>
          );

        case ELEMENT_TYPES.SECTION:
          return (
            <div className="_form-group">
              <label>Section Title</label>
              <input
                name="sectionTitle"
                value={modalInputs.sectionTitle}
                onChange={handleModalInput}
                placeholder="Enter section title"
              />
            </div>
          );

        case ELEMENT_TYPES.MAIN_BODY_COPY:
          return (
            <div className="_form-group">
              <label>Main Body Copy</label>
              <textarea
                name="mainBodyCopy"
                value={modalInputs.mainBodyCopy}
                onChange={handleModalInput}
                placeholder="Enter main body copy"
                rows={4}
              />
              <div className="_output-type-selector">
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="paragraph"
                    checked={modalInputs.mainBodyCopyOutputType === 'paragraph'}
                    onChange={handleModalInput}
                  />
                  Paragraph
                </label>
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="list"
                    checked={modalInputs.mainBodyCopyOutputType === 'list'}
                    onChange={handleModalInput}
                  />
                  List
                </label>
                <label>
                  <input
                    type="radio"
                    name="mainBodyCopyOutputType"
                    value="center"
                    checked={modalInputs.mainBodyCopyOutputType === 'center'}
                    onChange={handleModalInput}
                  />
                  Center
                </label>
              </div>
            </div>
          );

        case ELEMENT_TYPES.BODY_IMAGE:
          return (
            <>
              <div className="_form-group">
                <label>Body Image URL</label>
                <input
                  name="bodyImage"
                  value={modalInputs.bodyImage}
                  onChange={handleModalInput}
                  placeholder="Enter image URL"
                />
              </div>
              <div className="_form-group">
                <label>Alt Text</label>
                <input
                  name="bodyImageAlt"
                  value={modalInputs.bodyImageAlt}
                  onChange={handleModalInput}
                  placeholder="Enter alt text for accessibility"
                />
              </div>
              <div className="_form-group">
                <label>Body Copy Title</label>
                <input
                  name="bodyCopyTitle"
                  value={modalInputs.bodyCopyTitle}
                  onChange={handleModalInput}
                  placeholder="Enter body copy title"
                />
              </div>
              <div className="_form-group">
                <label>Body Copy</label>
                <textarea
                  name="bodyCopy"
                  value={modalInputs.bodyCopy}
                  onChange={handleModalInput}
                  placeholder="Enter body copy"
                  rows={4}
                />
                <div className="_output-type-selector">
                  <label>
                    <input
                      type="radio"
                      name="bodyCopyOutputType"
                      value="paragraph"
                      checked={modalInputs.bodyCopyOutputType === 'paragraph'}
                      onChange={handleModalInput}
                    />
                    Paragraph
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="bodyCopyOutputType"
                      value="list"
                      checked={modalInputs.bodyCopyOutputType === 'list'}
                      onChange={handleModalInput}
                    />
                    List
                  </label>
                </div>
              </div>
            </>
          );

        case ELEMENT_TYPES.ACCORDION:
          return (
            <>
              <div className="_form-group">
                <label>Accordion Title</label>
                <input
                  name="accordionTitle"
                  value={modalInputs.accordionTitle}
                  onChange={handleModalInput}
                  placeholder="Enter accordion title"
                />
              </div>
              <div className="_form-group">
                <label>Accordion Copy</label>
                <textarea
                  name="accordionCopy"
                  value={modalInputs.accordionCopy}
                  onChange={handleModalInput}
                  placeholder="Enter accordion copy"
                  rows={4}
                />
                <div className="_output-type-selector">
                  <label>
                    <input
                      type="radio"
                      name="accordionCopyOutputType"
                      value="paragraph"
                      checked={modalInputs.accordionCopyOutputType === 'paragraph'}
                      onChange={handleModalInput}
                    />
                    Paragraph
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="accordionCopyOutputType"
                      value="list"
                      checked={modalInputs.accordionCopyOutputType === 'list'}
                      onChange={handleModalInput}
                    />
                    List
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="accordionCopyOutputType"
                      value="html"
                      checked={modalInputs.accordionCopyOutputType === 'html'}
                      onChange={handleModalInput}
                    />
                    HTML
                  </label>
                </div>
              </div>
            </>
          );

        case ELEMENT_TYPES.YOUTUBE_VIDEO:
          return (
            <div className="_form-group">
              <label>YouTube Video URL</label>
              <input
                name="youtubeUrl"
                value={modalInputs.youtubeUrl}
                onChange={handleModalInput}
                placeholder="Paste YouTube video URL"
              />
            </div>
          );

        case ELEMENT_TYPES.WIDGET_BLOCK:
          return (
            <div className="_form-group">
              <label>Widget Field Value</label>
              <input
                name="widgetField"
                value={modalInputs.widgetField}
                onChange={handleModalInput}
                placeholder="Enter widget field value"
              />
            </div>
          );

        case ELEMENT_TYPES.RAW_HTML:
          return (
            <div className="_form-group">
              <label>Raw HTML</label>
              <textarea
                name="rawHtml"
                value={modalInputs.rawHtml}
                onChange={handleModalInput}
                placeholder="Paste raw HTML here"
                rows={6}
              />
            </div>
          );

        case ELEMENT_TYPES.CTA_BUTTON:
          return (
            <>
              <div className="_form-group">
                <label>Button Label</label>
                <input
                  name="ctaButtonLabel"
                  value={modalInputs.ctaButtonLabel}
                  onChange={handleModalInput}
                  placeholder="Enter button text"
                />
              </div>
              <div className="_form-group">
                <label>Button URL</label>
                <input
                  name="ctaButtonUrl"
                  value={modalInputs.ctaButtonUrl}
                  onChange={handleModalInput}
                  placeholder="Enter URL (e.g., https://example.com)"
                />
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <div className="_modal-overlay" onClick={closeModal}>
        <div className="_modal-content" onClick={e => e.stopPropagation()}>
          <div className="_modal-header">
            <h3>Add {COMPONENT_TYPES.find(c => c.id === selectedComponent)?.label}</h3>
            <button className="_modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="_modal-body">
            {renderInputFields()}
          </div>
          <div className="_modal-footer">
            <button className="_modal-btn _modal-btn--cancel" onClick={closeModal}>
              Cancel
            </button>
            <button className="_modal-btn _modal-btn--add" onClick={() => addElement(selectedComponent)}>
              Add Component
            </button>
          </div>
        </div>
      </div>
    );
  };

    // HTML/CSS/JS export with outputType support and raw html
  const generateExport = () => {
    // Group consecutive body image blocks for export
    const groupedElements = [];
    let currentGroup = [];
    
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      
      if (element.type === ELEMENT_TYPES.BODY_IMAGE) {
        // Add to current group
        currentGroup.push(element);
      } else {
        // If we have a group, add it to groupedElements
        if (currentGroup.length > 0) {
          groupedElements.push({
            type: 'group',
            elements: currentGroup
          });
          currentGroup = [];
        }
        // Add the non-body-image element
        groupedElements.push(element);
      }
    }
    
    // Handle any remaining group at the end
    if (currentGroup.length > 0) {
      groupedElements.push({
        type: 'group',
        elements: currentGroup
      });
    }
    
    const html = groupedElements
      .map((item: any) => {
        if (item.type === 'group') {
          const groupHTML = item.elements.map((el: any) => {
            let bodyCopyHTML = '';
            if (el.data.outputType === 'list') {
              const items = el.data.copy
                .split('\n')
                .map((line: string) => line.trim().replace(/^[-•*]\s*/, ''))
                .filter((line: string) => line.length > 0);
              bodyCopyHTML = `<ul class="_list-output">${items
                .map((item: string) => `<li>${item}</li>`)
                .join('')}</ul>`;
            } else {
              bodyCopyHTML = `<div class="_body-copy">${el.data.copy.replace(/\n/g, '<br />')}</div>`;
            }
          
            return `<div class="_body-image-block">
              <img class="_body-image" src="${el.data.url}" alt="${el.data.alt || 'Body'}" />
              <div class="_body-copy-container">
                ${el.data.title ? `<h3 class="_body-copy-title">${el.data.title}</h3>` : ''}
                ${bodyCopyHTML}
              </div>
            </div>`;
          }).join('\n');
          
          return `<div class="_body-block-group">\n${groupHTML}\n</div>`;
        }
        
        // Handle non-grouped elements
        const el = item;
        switch (el.type) {
          case ELEMENT_TYPES.MAIN_HEADER:
            return `<h1 class="_main-header">${el.data.title}</h1>`;
          case ELEMENT_TYPES.HEADER_IMAGE:
            return `<img class="_header-image" src="${el.data.url}" alt="${el.data.alt || 'Header'}" />`;
          case ELEMENT_TYPES.SECTION:
            return `<h2 class="_section-title">${el.data.title}</h2>`;
          case ELEMENT_TYPES.MAIN_BODY_COPY:
            if (el.data.outputType === 'list') {
              const items = el.data.copy
                .split('\n')
                .map((line: string) => line.trim().replace(/^[-•*]\s*/, ''))
                .filter((line: string) => line.length > 0);
              return `<ul class="_list-output">${items.map((item: string) => `<li>${item}</li>`).join('')}</ul>`;
            }
            if (el.data.outputType === 'center') {
              return `<p class="_main-body-copy" style="text-align:center">${el.data.copy.replace(/\n/g, '<br />')}</p>`;
            }
            return `<p class="_main-body-copy">${el.data.copy.replace(/\n/g, '<br />')}</p>`;
            case ELEMENT_TYPES.ACCORDION:
              let accordionCopyHTML = '';
              if (el.data.outputType === 'list') {
                const items = el.data.copy
                  .split('\n')
                  .map((line: string) => line.trim().replace(/^[-•*]\s*/, ''))
                  .filter((line: string) => line.length > 0);
                accordionCopyHTML = `<ul class="_list-output">${items
                  .map((item: string) => `<li>${item}</li>`)
                  .join('')}</ul>`;
              } else if (el.data.outputType === 'html') {
                accordionCopyHTML = `<div class="_accordion-copy">${el.data.copy}</div>`;
              } else {
                accordionCopyHTML = `<div class="_accordion-copy">${el.data.copy.replace(/\n/g, '<br />')}</div>`;
              }
              return `<div class="_accordion"><div class="_accordion-header"><span class="_accordion-title">${el.data.title}</span><button class="_accordion-toggle" aria-label="Expand/Collapse">+</button></div><div class="_accordion-body" style="display:none;">${accordionCopyHTML}</div></div>`;
          case ELEMENT_TYPES.YOUTUBE_VIDEO:
            const match = el.data.url.match(
              /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([\w-]{11})/
            );
            const videoId = match ? match[1] : null;
            return videoId
              ? `<div class="_youtube-video"><iframe width="100%" height="382" src="https://www.youtube.com/embed/${videoId}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="max-width:680px;width:100%"></iframe></div>`
              : '';

          case ELEMENT_TYPES.WIDGET_BLOCK:
            return `<div style="max-width: 980px !important; width: 100% !important; margin: 0 auto 64px !important; display: flex ;"><div id="Trending_Products" class="_tabcontent _widget_item_section">{{WidgetTemplate=PB-Homepage-Widget,${el.data.field}}}</div></div>`;
          case ELEMENT_TYPES.RAW_HTML:
            return `<div class="_raw-html-content">${el.data.html}</div>`;
        case ELEMENT_TYPES.CTA_BUTTON:
          return `<div class="_cta-button-container"><a href="${el.data.url}" class="_cta-button">${el.data.label}</a></div>`;
            default:
              return '';
        }
      })
      .join('\n');
    const css = `
@media screen {
  @font-face {
      font-family: 'tungsten';
      font-style: normal;
      src: local('tungsten Regular'), local('tungsten-Regular'), url(https://www.performancebike.com/content/skins/performancebike/fonts/Tungsten-Medium.woff) format('woff');
      font-display: swap;
  }
}

@media screen {
  @font-face {
      font-family: 'UniversLTStd-Cn';
      font-style: normal;
      src: local('UniversLTStd-Cn Regular'), local('UniversLTStd-Cn-Regular'), url(https://www.performancebike.com/content/skins/performancebike/fonts/UniversLTStd-Cn.woff2) format('woff');
      font-display: swap;
  }
}


._template-builder-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
}

._template-builder-form {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 2rem;
  min-width: 320px;
  max-width: 350px;
  flex: 1 1 320px;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

._template-builder-form h2 {
  margin: 16px 0 1.5rem;
  letter-spacing: 0.5px;
  font-family: 'tungsten';
  font-size: 2rem;
}

._form-group {
  margin-bottom: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}


._form-group input,
._form-group textarea {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

._form-group button {
  align-self: flex-end;
  padding: 0.4rem 1.2rem;
  background: #464647;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition:  0.2s;
}

._form-group button:hover {
  background: #000;
  box-shadow: 0px 4px 16px 0 rgba(0,0,0,0.16);
}

._elements-list {
  margin: 1.5rem 0;
}

._element-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7f7f7;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

._element-row button {
  background: #E04F26;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.2rem 0.8rem;
  cursor: pointer;
  font-size: 0.95rem;
}

._export-buttons {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: center;
}

._export-buttons button {
  align-self: flex-end;
  padding: 0.4rem 1.2rem;
  border: 1px solid #464647;
  background: #f9f9f9;
  color: #464647;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition:  0.2s;
 }

 ._export-buttons button:hover {
  background: #464647;
  color: #fff;
  box-shadow: 0px 4px 16px 0 rgba(0,0,0,0.16);
 }

._copied-msg {
  color: #28a745;
  font-size: 0.95rem;
  margin-left: 0.5rem;
}

._template-builder-preview {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  padding: 1rem;
  width: 100%;
  max-width: 1000px;
  border: 1px solid #fafbfc;
  background: #fff;
}

._preview-header{
  display: flex;
  background-color: #f9f9f9;
}

._template-builder-preview h3 {
font-size: 1.75rem;
letter-spacing: 0.5px;
width:60%;
padding: 16px;
margin: 0;
border-radius: 6px;
font-family: 'tungsten', sans-serif;
display: flex;
align-items: center;
}

._preview-controls {
display: flex;
justify-content: space-between;
align-items: center;
margin:0;
padding: 1rem;
background: #f9f9f9;
border-radius: 6px;
width:100%;
}

._add-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #464647;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s;
}

._add-button:hover {
  background: #000;
  box-shadow: 0px 4px 16px 0 rgba(0,0,0,0.16);
}

._add-icon {
  font-size: 1.2rem;
  font-weight: bold;
}

._export-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

._export-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #464647;
  background: #f9f9f9;
  color: #464647;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
}

._export-btn:hover {
  background: #464647;
  color: #fff;
  box-shadow: 0px 4px 16px 0 rgba(0,0,0,0.16);
}

._element-container {
  position: relative;
  margin-bottom: 1rem;
}

._remove-element-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: rgba(224, 79, 38, 0.9);
  color: #fff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
  height: 24px;
  width: 24px;
}

._element-container:hover ._remove-element-btn {
  opacity: 1 !important;
}

._remove-element-btn:hover {
  background: rgba(224, 79, 38, 1);
  transform: scale(1.1);
}

/* Modal Styles */
._modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

._modal-content {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

._modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

._modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-family: 'tungsten', sans-serif;
  color: #333;
}

._modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

._modal-close:hover {
  background: #f0f0f0;
}

._modal-body {
  padding: 1.5rem;
}

._modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem;
  border-top: 1px solid #eee;
}

._modal-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.2s;
}

._modal-btn--cancel {
  background: #f9f9f9;
  color: #666;
  border: 1px solid #ddd;
}

._modal-btn--cancel:hover {
  background: #f0f0f0;
}

._modal-btn--add {
  background: #464647;
  color: #fff;
  border: 1px solid #464647;
}

._modal-btn--add:hover {
  background: #000;
  box-shadow: 0px 4px 16px 0 rgba(0,0,0,0.16);
}

/* Component Grid */
._component-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
}

._component-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem 1rem;
  background: #f9f9f9;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

._component-option:hover {
  background: #f0f0f0;
  border-color: #464647;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

._component-icon {
  font-size: 2rem;
}

._component-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
}

/* Form styles for modal */
._form-group {
  margin-bottom: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

._form-group label {
  font-weight: 500;
  color: #333;
  font-size: 0.9rem;
}

._form-group input,
._form-group textarea {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  transition: border-color 0.2s;
}

._form-group input:focus,
._form-group textarea:focus {
  outline: none;
  border-color: #464647;
}

._output-type-selector {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

._output-type-selector label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
}

._output-type-selector input[type="radio"] {
  margin: 0;
}

._copied-msg {
  color: #28a745;
  font-size: 0.9rem;
  font-weight: 500;
}

._template-preview {
  width: 100%;
}

._main-header {
  font-size: 2.5rem;
  text-align: center;
  margin: 1rem 0;
  text-transform: uppercase;
  font-family: 'tungsten', sans-serif; 
}

._header-image {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  display: block;
  margin: 0 auto;
}

._main-body-copy {
  max-width: 680px;
  width: 100%;
  margin: 0 auto 32px !important;
}

._section-title {
  font-family: 'tungsten', sans-serif; 
  font-size: 2.5rem !important;
  margin: 2.5rem auto 1rem auto !important;
  text-align: center;
  max-width: 680px;
  width: 100%;
} 

._body-copy-title {
  font-size: 1.75rem !important;
  font-family: 'tungsten', sans-serif;
  padding: 16px 0 !important;
  margin: 0;
  text-align: left !important;
  justify-content: left !important;
}

._body-copy-container{
  flex: 1 1 0%;
}

._body-image-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 1.5rem auto 32px;
  max-width: 680px;
  width: 100%;
}

._body-image {
  width: 300px;
  max-width: 100%;
  margin-right: 1rem;
}

._body-copy {
  flex: 1;
  min-width: 150px;
}

._body-copy > br{
  display: none;
}


._youtube-video {
  max-width: 680px;
  width: 100%;
  margin: 16px auto 64px;
  display: block;
}

._accordion {
  max-width: 680px;
  margin: 0px auto;
  font-size: 1rem;
  width: 100%;
  border: none;
  border-radius: 0em !important;
  text-align: left !important;
  outline: none;
  transition: 0.4s;
  height: auto !important;
  min-height: 50px;
  border-top: 1px solid #f1f1f1;
  line-height: 28px;

}
._accordion-header {
  font-size: 1.5rem;
  letter-spacing: .05rem;
  color: #000 !important;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1.5rem 1.5rem;
  cursor: pointer;
  background: #fafafa;
  transition: background 0.4s;
  border: none;
  outline: none;

}
._accordion-header:hover, ._accordion-header:focus {
      background-color: #f9f9f9 !important;
}
._accordion-title {
  font-size: 1.75rem;
  font-family: 'tungsten', sans-serif;
  font-weight: 600;
  letter-spacing: 0.01em;
  flex: 1;
  user-select: none;
  font-weight: 500;
}
._accordion-toggle {
        color: #777;
        border: 1px solid #777;
        font-weight: bold;
        float: right;
        margin-left: 5px;
        border-radius: 50%;
        transition: all 0.3s ease;
        height: 32px;
        width: 32px;
        background: #f2f2f2;
        line-height: 1.9rem;
}

._accordion-toggle:active {
  background: #E04F26;
  color: #fff;
  border: 1px solid #E04F26;
}

._accordion-toggle:hover {
  color: #d3d3d3;
  border: 1px solid #d3d3d3;

}
._accordion-body {
  padding: 1.5rem 1.5rem;
  background: #fff;
  animation: accordion-fade-in 0.3s;
  transition: max-height 0.6s cubic-bezier(0.4,0,0.2,1), padding 0.6s;
}

ul._list-output{
  padding-left: 16px;
  margin-top: 0;
}

ul._list-output li {
  margin-left: 0 !important;
  padding-bottom: 16px !important;
}

._raw-html-content {
max-width: 680px;
width: 100%;
margin: 0 auto 32px !important;
}

._raw-html-content h3{
  font-family: 'tungsten', sans-serif;
  font-size: 1.75rem;
  padding: 16px 0;
  letter-spacing: 0.5px;
  background-color: #fff;
  }
  ._raw-html-content ul {
    margin: 0;
    padding: 0 0 16px;
  }
  
  ._raw-html-content strong{
      padding-right: 16px;
  }

  ._raw-html-content li {
    line-height: 1.5rem;
    padding-top: 4px;
    padding-left: 6px;
  }
  ._raw-html-content ul :nth-child(even) {
    background: #f9f9f9;
  }


@keyframes accordion-fade-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
._accordion-copy {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.6;
  color: #222;
}

/* HTML option list striping inside accordion and generic html output */
._accordion-copy ul li:nth-child(even),
._html-output ul li:nth-child(even) {
  background-color: #f2f2f2;
}

._accordion-copy ul li:nth-child(odd),
._html-output ul li:nth-child(odd) {
  background-color: #ffffff;
}

._accordion-toggle--active {
  background: #E04F26;
  color: #fff;
  border: 1px solid #E04F26;
  line-height: 0 !important;
}

._widget_item_section {
    max-width: 980px !important;
}
    ._tabcontent {
    max-width: 980px;
    width: 100%;
    margin: 0 auto 64px;
    display: flex;
}

.__carousel__ { max-width: 980px !important;}

@media (max-width: 900px) {
  ._template-builder-container {
    flex-direction: column;
    align-items: stretch;
  }
  ._template-builder-form, ._template-builder-preview {
    max-width: 100%;
    min-width: 0;
  }
}

@media (max-width: 600px) {
  ._body-image-block {
    flex-direction: column;
    align-items: flex-start;
  }
  ._body-image {
    margin-right: 0;
    margin-bottom: 1rem;
    width: 100%;
  }
  ._accordion-header {
    font-size: 1.2rem;
    padding: 1rem 1rem 1rem 1rem;
    min-height: 48px;
  }
  ._accordion-title {
    font-size: 1.75rem;
  }
  ._accordion-toggle {
    width: 32px;
    height: 32px;
    font-size: 1.2rem;
    scale: 0.8;
  }
  ._accordion-body {
    padding: 1rem 1rem 1rem 1rem;
  }

  ._tab {
    flex-direction: column;
    align-items: center;
}

._tab button {
    font-size: inherit;
    width: 100%;
}

a._view_all_arrow {
    float: none;
    text-decoration: none;
    text-align: center;
    justify-content: center;
    margin-top: 32px;
    display: block !important;
}

._view_all_arrow:after {
    padding-top: 2px;
}

._recommended-accessory-marketing-label {
    display: none;
}

._widget_item_section {
    max-width: 376px !important;
}


} 

a._view_all_arrow {
    float: right;
    text-decoration: none;
}

a._view_all_arrow:hover {
    text-decoration: underline;
    color: #E04F26;
}

._view_all_arrow:after {
    vertical-align: middle;
    display: inline-block;
    font: normal normal normal 30px / 1 "Font Awesome 6 Pro";
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\f105";
    font-weight: 900;
    margin-left: 6px;
    font-size: 1.2rem;
    padding-bottom: 2px;
}

._recommended-accessory-marketing-label {
    width: 220px;
    height: auto;
    max-height: 290px;
    position: relative;
    margin: 9px 16px 0 0;
    outline-offset: -2px;
    overflow: hidden;
}

._recommended-accessory-marketing-label:hover {
    box-shadow: 6px 6px 6px 2px #0c0b082e;
    transform: translateY(-.1rem);
    transition: .3s;
}

._recommended-accessory-marketing-label:hover ._shop_now_cta {
    color: #E04F26;
}

._recommended-accessory-marketing-label img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

._recommended-accessory-marketing-label section {
    position: absolute;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 100%;
    padding: 16px 0 0;
}

._recommended-accessory-marketing-label p {
    font-family: 'UniversLTStd-Cn', sans-serif;
    color: #fff;
    padding: 8px;
    margin: 0 !important;
    text-shadow: 0 0 8px #000000c4;
    font-size: 0.9rem !important;
    text-align: left;
    letter-spacing: 0.5px;
}

._recommended-accessory-marketing-label ._shop_now_cta {
    font-family: 'UniversLTStd-Cn', sans-serif;
    color: #fff;
    margin: 0 auto 8px 0;
    text-shadow: 0 0 8px #000000c4;
    font-size: 1rem;
    width: 100%;
    text-align: center;
    position: relative;
}

._recommended-accessory-marketing-label ._shop_now_cta:after {
    vertical-align: middle;
    display: inline-block;
    font: normal normal normal 30px / 1 "Font Awesome 6 Pro";
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\f105";
    font-weight: 900;
    margin-left: 6px;
    font-size: 1.2rem;
    padding-bottom: 2px;
}

.recommended-accessory-marketing {
    background-color: #fff;
}

.recommended-accessory-marketing:hover {
    background: transparent linear-gradient(180deg, #46464700 0%, #4746461c 100%) 0% 0% no-repeat padding-box;
    mix-blend-mode: multiply;
}

.recommended-accessory-marketing-copy {
    mix-blend-mode: multiply;
}

.recommended-accessory-marketing-copy:hover {
    background-color: #f2f2f2;
} 

._main-body-copy ul,
._body-copy ul {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

._main-body-copy li,
._body-copy li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

._cta-button-container {
  text-align: center;
  height: 100px;
}

  

._template-builder-preview ._element-container:nth-child(even) ._body-image-block {
  background-color: #f9f9f9;
  flex-direction: row-reverse;
    padding-left: 32px;
}

._template-builder-preview ._element-container:nth-child(even) ._body-image {
  margin-right: 0;
  margin-left: 1rem;
}

._cta-button-container a{
  background-color: #2D2D2E;
    box-sizing: border-box;
    color: white;
    display: block;
    margin: auto;
    max-width: 515px;
    text-decoration: none;
    text-transform: uppercase;
    padding: 30px 32px 26px;
}

._cta-button-container a:hover {
  background-color: #000;
} 

._cta-button {
  background-color: #2D2D2E;
  box-sizing: border-box;
  color: white;
  display: block;
  margin: auto;
  max-width: 515px;
  text-decoration: none;
  text-transform: uppercase;
  padding: 30px 32px 26px;
}

._cta-button:hover {
  background-color: #000;
}

/* Container block styles for consecutive body copy elements */
._element-container-block {
  max-width: 680px;
  width: 100%;
  margin: 0 auto 32px;
}

/* Body image group styles for consecutive body image blocks */
._body-image-group {
  max-width: 680px;
  width: 100%;
  margin: 0 auto 32px;
}

._body-block-group ._body-image-block:nth-child(even){
  background-color: #f9f9f9;
  flex-direction: row-reverse;
  }
  
  ._body-block-group ._body-image-block:nth-child(even) ._body-copy-title{
      background-color: none !important;
  }
  
  ._body-block-group ._body-image-block:nth-child(even) ._body-copy-container{
       padding-left: 32px;
  }
  
      ._body-block-group ._body-image-block:nth-child(even) ._body-image{
      margin-right: 0;
  }
  

/* Responsive adjustments */
@media (max-width: 598px) {
  ._preview-controls {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  ._add-button {
    justify-content: center;
  }
  
  ._export-controls {
    justify-content: center;
  }
  
  ._component-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 0.75rem;
  }
  
  ._component-option {
    padding: 1rem 0.75rem;
  }
  
  ._component-icon {
    font-size: 1.5rem;
  }
  
  ._component-label {
    font-size: 0.8rem;
  }
  
  ._modal-content {
    margin: 1rem;
    max-height: 95vh;
  }

  ._preview-header {
    flex-direction: column;
    text-align: center;
}
._template-builder-preview h3{
    width: auto;
    justify-content: center;
 }
 ._preview-controls {
    width: auto;
 }

 ._template-builder-preview ._element-container:nth-child(even) ._body-image-block {
  background-color: #fff;
  flex-direction: row-reverse;
    padding-left: 0;
}
._body-block-group ._body-image-block:nth-child(even){
  background-color: #fff;
}

  ._body-block-group ._body-image-block:nth-child(even) ._body-copy-container{
padding-left: 0;
}

}

._element-controls {
  display: flex;
  gap: 4px;
}
/* Re-order arrow buttons */
.arrow-btn {
  width: auto;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #bfbfbf;
  background-color: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

._accordion-copy ul{
  padding-left: 0;
}

`;
    const js = `

    document.addEventListener('DOMContentLoaded', function() {
  const accordions = document.querySelectorAll('._accordion');

  accordions.forEach((accordion) => {
    const header = accordion.querySelector('._accordion-header');
    const body = accordion.querySelector('._accordion-body');
    const toggle = accordion.querySelector('._accordion-toggle');

    // Make the entire header clickable
    header.addEventListener('click', function() {
      const isOpen = body.style.display === 'block';

      // Close all other accordions if desired (optional)
      // accordions.forEach(acc => {
      //   acc.querySelector('._accordion-body').style.display = 'none';
      //   acc.querySelector('._accordion-toggle').textContent = '+';
      //   acc.querySelector('._accordion-toggle').classList.remove('_accordion-toggle--active');
      // });

      // Toggle this one
      if (isOpen) {
        body.style.display = 'none';
        toggle.textContent = '+';
        toggle.classList.remove('_accordion-toggle--active');
      } else {
        body.style.display = 'block';
        toggle.textContent = '-';
        toggle.classList.add('_accordion-toggle--active');
      }
    });
  });
});


(function() {
  var toggles = document.querySelectorAll('._accordion-toggle');
  toggles.forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.stopPropagation();
      var accordion = btn.closest('._accordion');
      var body = accordion.querySelector('._accordion-body');
      var allAccordions = document.querySelectorAll('._accordion');
      allAccordions.forEach(function(acc) {
        var accBody = acc.querySelector('._accordion-body');
        var accBtn = acc.querySelector('._accordion-toggle');
        if (acc !== accordion) {
          accBody.style.display = 'none';
          accBtn.textContent = '+';
          accBtn.classList.remove('_accordion-toggle--active');
        }
      });
      if (body.style.display === 'block') {
        body.style.display = 'none';
        btn.textContent = '+';
        btn.classList.remove('_accordion-toggle--active');
      } else {
        body.style.display = 'block';
        btn.textContent = '-';
        btn.classList.add('_accordion-toggle--active');
      }
    });
  });
})();
`;
    return { html, css, js };
  };

  const handleCopyAll = () => {
    const { html, css, js } = generateExport();
    const allCode = `<style>\n${css}\n</style>\n\n${html}\n\n<script>\n${js}\n</script>`;
    navigator.clipboard.writeText(allCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className="_template-builder-container">
      <div className="_template-builder-preview">
      {renderPreview()}
        <div className="_preview-header">
          <h3>Template Builder</h3>
          <div className="_preview-controls">
            <button className="_add-button" onClick={openComponentList}>
              <span className="_add-icon">+</span>
              <span>Add Component</span>
            </button>
            <div className="_export-controls">
              <button className="_export-btn" onClick={handleCopyAll}>
                Copy All Code
              </button>
              {copied && <span className="_copied-msg">Copied!</span>}
            </div>
          </div>
        </div>

      </div>
      {renderComponentListModal()}
      {renderInputModal()}
    </div>
  );
};

export default TemplateBuilder;
